import { SxProps, Theme } from '@mui/material';
import { sharedCardStyles } from '../SharedCard.styles';
import { TEXT_DARK } from '../../../../styles/GlobalStyles';

/**
 * Styles for Experience Card component
 */

const CARD_TAG_HEIGHT = '28px';
const RIGHT_RAIL_WIDTH = '152px';

export const masterTemplateCardStyles = {
  card: (theme: Theme, standardsExpectationHeight: number = 0) =>
    ({
      ...sharedCardStyles.card(theme, { direction: 'row', cursor: 'pointer' }),
      alignItems: 'flex-start',
      paddingBottom: standardsExpectationHeight > 0 ? `${standardsExpectationHeight}px` : 0,
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
      '&:hover .assign-link, &:hover .license-to-assign-link, &:hover .preview-link, &:hover .review-link': {
        opacity: 1,
        visibility: 'visible',
      },
    }) as SxProps<Theme>,

  linksContainer: (theme: Theme) =>
    ({
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      zIndex: 1,
    }) as SxProps<Theme>,


  reviewLink: (theme: Theme) =>
    ({
      ...sharedCardStyles.actionLink(theme, {
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
      }),
    }) as SxProps<Theme>,

  reviewIcon: (theme: Theme) =>
    ({
      width: '16px',
      height: '16px',
      color: theme.palette.primary.main,
    }) as SxProps<Theme>,

  previewLink: (theme: Theme) =>
    ({
      ...sharedCardStyles.actionLink(theme, {
        color: '#555555',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        hoverTextDecoration: 'none',
      }),
    }) as SxProps<Theme>,

  previewIcon: (theme: Theme) =>
    ({
      width: '16px',
      height: '16px',
      color: theme.palette.primary.main,
    }) as SxProps<Theme>,

  assignLink: (theme: Theme, disabled: boolean = false) =>
    ({
      ...sharedCardStyles.actionLink(theme, {
        color: '#555555',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1),
        hoverTextDecoration: 'none',
      }),
      ...(disabled ? {
        color: theme.palette.text.disabled,
        pointerEvents: 'none',
        cursor: 'default',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'none',
        },
      } : {}),
    }) as SxProps<Theme>,

  assignIcon: (theme: Theme) =>
    ({
      width: '14px',
      height: '14px',
      color: theme.palette.primary.main,
    }) as SxProps<Theme>,

  imageContainer: (theme: Theme) =>
    ({
      ...sharedCardStyles.imageContainer(theme),
    }) as SxProps<Theme>,

  image: (_theme: Theme) =>
    ({
      ...sharedCardStyles.image(),
    }) as SxProps<Theme>,

  contentContainer: (theme: Theme) =>
    ({
      ...sharedCardStyles.contentContainer(theme, theme.spacing(1, 8, 1, 1.25)),
      minHeight: '120px',
    }) as SxProps<Theme>,

  title: (theme: Theme) =>
    ({
      ...sharedCardStyles.title(theme),
    }) as SxProps<Theme>,

    description: (theme: Theme, maxLines: number = 2) => {
      return ({
        ...sharedCardStyles.description(theme, maxLines),
      }) as SxProps<Theme>;
    },

  labelsAndStandardsContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginTop: 'auto',
      minHeight: '28px',
      width: '100%',
    }) as SxProps<Theme>,

  labelsContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      flexShrink: 0,
    }) as SxProps<Theme>,

  labelBadge: (theme: Theme, labelValueId?: number) => {
    let backgroundColor = '#F7F7F7';
    let color = TEXT_DARK;
    if (labelValueId === 1) {
      backgroundColor = '#8DC153';
      color = '#fff';
    } else if (labelValueId === 2) {
      backgroundColor = '#F7F7F7';
      color = TEXT_DARK;
    }

    return ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: CARD_TAG_HEIGHT,
      boxSizing: 'border-box',
      backgroundColor,
      color,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      padding: theme.spacing(0, 1),
      borderRadius: '3px',
      border: '2px solid #929292',
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>;
  },

  standardsContainer: (theme: Theme) =>
    ({
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    }) as SxProps<Theme>,

  badgeContainer: (theme: Theme) =>
    ({
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      flexWrap: 'nowrap',
      flexShrink: 0,
      minWidth: RIGHT_RAIL_WIDTH,
      gap: theme.spacing(1),
      marginLeft: 'auto',
      marginRight: theme.spacing(-7),
    }) as SxProps<Theme>,

  badge: (theme: Theme) =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: CARD_TAG_HEIGHT,
      boxSizing: 'border-box',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      padding: theme.spacing(0, 1.5),
      borderRadius: '3px',
      whiteSpace: 'nowrap',
    }) as SxProps<Theme>,
};

export default masterTemplateCardStyles;
